import { useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import { Button } from '@shared/components/ui/button';
import { ModalCenter, ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { TextArea } from '@shared/components/ui/textarea';
import { useToast } from '@shared/hooks/use-toast';
import { getErrorMessage } from '@shared/utils/error.utils';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWithdrawalsMutations } from '../../hooks/useWithdrawalsMutations';
import type { Withdrawal } from '../../models';

export interface RejectWithdrawalModalRef {
  open: (withdrawal: Withdrawal) => void;
  close: () => void;
}

interface RejectWithdrawalModalProps {
  onSuccess?: () => void;
}

export const RejectWithdrawalModal = forwardRef<RejectWithdrawalModalRef, RejectWithdrawalModalProps>(
  ({ onSuccess }, ref) => {
    const { t } = useTranslation('withdrawalsModule');
    const { toast } = useToast();
    const currency = useCurrency();
    const modalRef = useRef<ModalCenterRef>(null);
    const { rejectWithdrawal } = useWithdrawalsMutations();

    const [withdrawal, setWithdrawal] = useState<Withdrawal | null>(null);
    const [reason, setReason] = useState('');

    useImperativeHandle(ref, () => ({
      open: (w) => {
        setWithdrawal(w);
        setReason('');
        modalRef.current?.openModal();
      },
      close: () => modalRef.current?.closeModal(),
    }));

    const handleConfirm = async () => {
      if (!withdrawal || !reason.trim()) return;
      try {
        await rejectWithdrawal.mutateAsync({
          id: withdrawal.id,
          payload: { rejection_reason: reason.trim() },
        });
        toast({
          title: t('modals.reject.success.title'),
          description: t('modals.reject.success.description'),
        });
        modalRef.current?.closeModal();
        onSuccess?.();
      } catch (error) {
        toast({
          title: t('errors.rejectFailed'),
          description: getErrorMessage(error),
          variant: 'destructive',
        });
      }
    };

    const isLoading = rejectWithdrawal.isPending;

    return (
      <ModalCenter ref={modalRef} title={t('modals.reject.title')} width="default">
        <div className="space-y-4">
          <p className="text-sm text-foreground">{t('modals.reject.description')}</p>

          {withdrawal && (
            <div className="bg-muted rounded-lg p-3 space-y-1">
              <p className="text-sm">
                <span className="font-medium">ID:</span> #{withdrawal.id}
              </p>
              <p className="text-sm">
                <span className="font-medium">Importe:</span> {withdrawal.requested_amount} {currency}
              </p>
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">{t('modals.reject.fields.reason')} *</label>
            <TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={isLoading}
              rows={3}
              placeholder={t('modals.reject.fields.reason_placeholder')}
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => modalRef.current?.closeModal()} disabled={isLoading}>
              {t('buttons.cancel')}
            </Button>
            <Button variant="destructive" onClick={handleConfirm} disabled={!reason.trim() || isLoading}>
              {isLoading ? t('buttons.rejecting') : t('actions.reject')}
            </Button>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

RejectWithdrawalModal.displayName = 'RejectWithdrawalModal';
